import React, { useEffect, useState } from "react";
import { Button, Card, Col, Modal, Row, Spinner } from "react-bootstrap";
import Standard from "../my-cases/my-case/notes/Standard";
import Contact from "../my-cases/my-case/notes/Contact";
import { User } from "../../types/user";
import { postNote, postNoteDocument, postSummary } from "../../services/httpClient";
import SelectObject from "./SelectObject";
import Intervention from "../my-cases/my-case/notes/Intervention";
import Referral from "../my-cases/my-case/notes/Referral";
import StudentConnections from "../my-cases/my-case/notes/StudentConnections";
import Infraction from "../my-cases/my-case/notes/Infraction";
import CustomNote from "../my-cases/my-case/notes/CustomNote";
import { useNavigate } from "react-router-dom";

interface CreateNoteState {
  showModal: boolean;
  setShowModal: any;
  caseId: string;
  name: string;
  person?: any;
  family?: User[];
  types: any;
  setUserToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  limitedAccess: boolean;
}

const CreateNoteModal = ({
  showModal,
  setShowModal,
  caseId,
  name,
  person,
  family,
  types,
  setUserToSave,
  setShowToast,
  setToastText,
  setToastBody,
  limitedAccess,
}: CreateNoteState) => {
  const [type, setType] = useState<string>("");
  const [typeObject, setTypeObject] = useState<any>({});
  const [saveIsEnabled, setSaveIsEnabled] = useState<boolean>(false);
  const [newNote, setNewNote] = useState<any>({});
  const [docs, setDocs] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [selectedMember, setSelectedMember] = useState<User[]>([]);

  let navigate = useNavigate();

  useEffect(() => {
    if (person) {
      const personObject = {
        id: person?.user_id ? person?.user_id : person.id,
        type: person?.user_id ? "guardian" : "member",
        role: "",
        firstname: person?.name,
        lastname: "",
        name: person?.name,
      };
      setSelectedMember([personObject]);
    } else if (family && family.length > 0) {
      setSelectedMember(family);
    }
  }, [person, family]);

  const updateType = (e: any) => {
    setType(e.target.value);
    var fullType = types?.find((t: any) => t.key === e.target.value);
    setTypeObject(fullType);
    setSaveIsEnabled(false);
  };

  const onClickSave = () => {
    setIsSaving(true);
    postNote(newNote, caseId)
      .then((res: any) => {
        const noteId = res[0].data.note_id;
        if (newNote.notetype === "standard" && docs.length > 0) {
          const length = docs.length;
          var counter = 0;
          docs.map((d: any) => {
            counter++;
            const name = d.name;
            const data = d.data;

            postNoteDocument(name, data, caseId, noteId).then((res: any) => {
              if (counter === length) {
                setUserToSave(false);
                setShowToast(true);
                setToastText("Note Created");
                setToastBody(title(newNote.notetype));
              }
            });
          });
        } else {
          setUserToSave(false);
          setShowToast(true);
          setToastText("Note Created");
          setToastBody(newNote?.type === "custom" ? newNote.notetypename : title(newNote.notetype));
        }

        setNewNote({});
        setDocs([]);
        setType("");
        setSaveIsEnabled(false);
        setIsSaving(false);
        setShowModal(false);

        postSummary(caseId).catch((err: any) => {
          console.log(err);
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const title = (type: any) => {
    if (type === "student") {
      return "Student Connections";
    } else if (type === "intervention") {
      return "Intervention Follow-Up";
    } else {
      return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const onClickNotes = (e: any) => {
    e.stopPropagation();
    navigate(`/my-cases/${caseId}?key=notes`);
  };

  return (
    <>
      <Modal show={showModal} onHide={handleClose} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{"Create Note for " + name} </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card className="bg-light m-0">
            <Row className="p-3">
              <Col xs={6} lg={6}>
                <SelectObject label="Note Type" value={type} options={types} onUpdate={(e: any) => updateType(e)} />
              </Col>
              <Col xs={3} lg={3}></Col>
              <Col xs={3} className="d-flex justify-content-end align-items-center">
                {!limitedAccess && (
                  <>
                    <Button className="app-primary-color button" onClick={(e) => onClickNotes(e)}>
                      View Case Notes
                    </Button>
                    <div className="vr mx-3" />
                  </>
                )}
                <Button className="app-secondary-color button" onClick={() => onClickSave()} disabled={!saveIsEnabled}>
                  Save
                </Button>
              </Col>
            </Row>
          </Card>
          {isSaving ? (
            <Row className="pt-3">
              <Button size="sm" className="spinner-button">
                <Spinner animation="border" style={{ color: "#F6893D" }} />
              </Button>
            </Row>
          ) : (
            <>
              {type === "standard" ? (
                <Standard
                  family={selectedMember}
                  documents={docs}
                  setDocuments={setDocs}
                  setUpdatedNote={setNewNote}
                  setUserToSave={setUserToSave}
                  setSaveIsEnabled={setSaveIsEnabled}
                  myCase={true}
                />
              ) : type === "contact" ? (
                <Contact
                  family={selectedMember}
                  setUpdatedNote={setNewNote}
                  setUserToSave={setUserToSave}
                  setSaveIsEnabled={setSaveIsEnabled}
                  myCase={true}
                />
              ) : type === "intervention" ? (
                <Intervention
                  family={selectedMember}
                  setUpdatedNote={setNewNote}
                  setSaveIsEnabled={setSaveIsEnabled}
                  setUserToSave={setUserToSave}
                  myCase={true}
                />
              ) : type === "referral" ? (
                <Referral
                  family={selectedMember}
                  setUpdatedNote={setNewNote}
                  setSaveIsEnabled={setSaveIsEnabled}
                  setUserToSave={setUserToSave}
                  myCase={true}
                />
              ) : type === "student" ? (
                <StudentConnections
                  family={selectedMember}
                  setUpdatedNote={setNewNote}
                  setSaveIsEnabled={setSaveIsEnabled}
                  setUserToSave={setUserToSave}
                  myCase={true}
                />
              ) : type === "infraction" ? (
                <Infraction
                  family={selectedMember}
                  noteType={typeObject}
                  setUpdatedNote={setNewNote}
                  setSaveIsEnabled={setSaveIsEnabled}
                  setUserToSave={setUserToSave}
                  myCase={true}
                />
              ) : (
                /^\d+_/.test(type) && (
                  <CustomNote
                    key={type}
                    family={selectedMember}
                    type={typeObject}
                    setUpdatedNote={setNewNote}
                    setSaveIsEnabled={setSaveIsEnabled}
                    setUserToSave={setUserToSave}
                    myCase={true}
                  />
                )
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CreateNoteModal;
